import {AfterViewInit, Component, ElementRef, Injector, OnInit, ViewChild} from '@angular/core';
import {BaseComponent} from "../../base.component";
import {RouteUtil} from "../../../util/route.util";
import {DataResponse} from "../../../network/service/data.response";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {StringUtil} from "../../../util/string.util";
import {Config} from "../../../common/config";
import {strings} from "../../../../value/strings";
import {SessionStorageUtil} from "../../../util/session.storage.util";
import {isPlatformBrowser} from '@angular/common';
import {OtpComponent} from '../../base/otp/otp.component';
import {NotificationService} from '../../../util/service/notification.service';

@Component({
  selector: 'app-sign-in',
  standalone: false,
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent extends BaseComponent implements OnInit, AfterViewInit {
  recaptcha: any;
  mobileOrEmail: string = '';
  passwordMode?: boolean = false;
  otpMode?: boolean = false;
  timer: number = 0;
  intervalId: any;
  isTimerActive: boolean = false;
  codeOrPassword?: any;
  typeOfInput: any;
  @ViewChild('videoElement')
  video!: ElementRef<HTMLVideoElement>;
  videoIds = Config.domain.Details?.Videos ?? [];
  randomVideoUrl = '';
  isSubmitting = false;
  @ViewChild(OtpComponent) otpComponent!: OtpComponent;
  protected readonly Config = Config;

  constructor(injector: Injector, private el: ElementRef, private notificationService: NotificationService) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setRandomVideo();
  }

  onBackToHomeClicked() {
    this.redirectToHomePage();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.createStars(350);
  }

  onMobileInput(event: any) {
    let value = event.target.value;
    if (!value.startsWith('09')) {
      value = '09' + value.replace(/^0+/, '').replace(/\D/g, '');
    }
    value = value.replace(/\D/g, '').slice(0, 11);
    this.mobileOrEmail = value;
    event.target.value = value;
  }

  setRandomVideo() {
    this.randomVideoUrl = this.getRandomVideoUrl();
    if (isPlatformBrowser(Config.platformId)) {
      this.playVideoWithRetry();
    }
  }

  playVideoWithRetry(retries = 10, delay = 200) {
    if (retries <= 0) {
      return;
    }
    setTimeout(() => {
      if (this.video?.nativeElement) {
        const videoElement = this.video.nativeElement;
        videoElement.muted = true;
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            this.playVideoWithRetry(retries - 1, delay * 2);
          });
        } else {
          this.playVideoWithRetry(retries - 1, delay * 2);
        }
      } else {
        this.playVideoWithRetry(retries - 1, delay * 2);
      }
    }, delay);
  }

  onLicenseAgreementClicked() {
    this.router.navigate([RouteUtil.COMMON_MODULE, RouteUtil.LICENCE_AGREEMENT_COMPONENT]).then();
  }

  onGetVerifyCode(otpMode: any = false) {
    this.isSubmitting = true;
    if (this.mobileOrEmail) {
      this.typeOfInput = StringUtil.emailOrMobileValidator(this.mobileOrEmail);
      let loginInput: any = {};
      switch (this.typeOfInput) {
        case 'EMAIL':
          loginInput = {
            Email: this.mobileOrEmail,
            UseOTP: otpMode
          };
          break;
        case 'MOBILE':
          loginInput = {
            MobileNumber: this.mobileOrEmail,
            UseOTP: otpMode
          };
          break;
        default:
          this.isSubmitting = false;
          return SnackBarUtil.makeMessage(this, this.translateService.instant('MustUseMobileOrEmail'), false);
      }
      this.otpMode = otpMode;
      this.getCaptchaToken('authentication/login', (token: string) => {
        loginInput.Captcha = token;
        this.serviceApi.login(loginInput).subscribe((res: DataResponse<any>) => {
          this.isSubmitting = false;
          if (res.success) {
            this.passwordMode = true;
            if (res.data) {
              this.otpMode = true;
              const currentTime = new Date().getTime();
              const expireTime = res.data.VerificationCodeExpired;
              this.timer = Math.max(Math.floor((expireTime - currentTime) / 1000), 0);
              if (this.timer > 0) {
                this.startTimer();
              }
            }
          } else {
            SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
          }
        });
      });
    } else {
      this.isSubmitting = false;
      return SnackBarUtil.makeMessage(this, this.translateService.instant('MustUseMobile'), false);
    }
  }

  startTimer() {
    this.isTimerActive = true;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        this.isTimerActive = false;
        clearInterval(this.intervalId);
      }
    }, 1000);
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = remainingSeconds.toString().padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  }

  onSignInClicked() {
    this.isSubmitting = true;
    if (this.codeOrPassword) {
      this.typeOfInput = StringUtil.emailOrMobileValidator(this.mobileOrEmail);
      let loginInput: any = {};
      switch (this.typeOfInput) {
        case 'EMAIL':
          loginInput = {
            Email: this.mobileOrEmail,
            VerificationCode: this.otpMode ? this.codeOrPassword : null,
            Password: !this.otpMode ? this.codeOrPassword : null,
            UseOTP: this.otpMode
          };
          break;
        case 'MOBILE':
          loginInput = {
            MobileNumber: this.mobileOrEmail,
            VerificationCode: this.otpMode ? this.codeOrPassword : null,
            Password: !this.otpMode ? this.codeOrPassword : null,
            UseOTP: this.otpMode
          };
          break;
        default:
          this.isSubmitting = false;
          return SnackBarUtil.makeMessage(this, this.translateService.instant('FillUsernamePlease'), false);
      }
      this.getCaptchaToken('authentication/verify', (token: string) => {
        loginInput.Captcha = token;
        this.serviceApi.login(loginInput).subscribe((res: DataResponse<any>) => {
          this.isSubmitting = false;
          if (res.success) {
            this.addSessionStorageToBasket();
            if (isPlatformBrowser(Config.platformId) && this.authenticationService.hasAccount()) {
              this.notificationService.subscribeToNotifications();
            }
            this.serviceApi.getUserInformation().subscribe((res: DataResponse<any>) => {
              if (res.success) {
                Config.userInformation = res.data;
              } else {
                SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
              }
            });
            this.redirectToHomePage();
          } else {
            SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
          }
        });
      });
    } else {
      this.isSubmitting = false;
      if (this.otpMode) {
        SnackBarUtil.makeMessage(this, this.translateService.instant('CodeIsInvalid'), false);
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant('PasswordIsInvalid'), false);
      }
    }
  }

  getCaptchaToken(action: string, callback: any) {
    // load('6LcbykgrAAAAAG56m7xtborQ40eqRWhEqvF96HHQ').then((recaptcha) => {
    //   recaptcha.execute(action).then((token: string) => {
    callback('token');
    //   });
    // });
  }

  addSessionStorageToBasket() {
    let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
    for (const item of basket) {
      this.serviceApi.postOrders({
        ServicePriceId: item.ServicePriceId,
        Count: item.Count,
        ServicePriceAdditiveIds: item.ServicePriceAdditiveIds,
        Type: strings.basket
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    }
    SessionStorageUtil.remove(strings.basket_service_ids);
  }

  redirectToHomePage() {
    this.router.navigate([RouteUtil.CLIENT_MODULE]).then();
  }

  createStars(count: number) {
    if (isPlatformBrowser(Config.platformId)) {
      const container = this.el.nativeElement.querySelector('.bg-login');
      const pageHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      for (let i = 0; i < count; i++) {
        const star = this.renderer.createElement('div');
        this.renderer.addClass(star, 'star');
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * pageHeight;
        const size = Math.random() * 2 + 1;
        const duration = Math.random() * 1.5 + 0.5;
        this.renderer.setStyle(star, 'width', `${size}px`);
        this.renderer.setStyle(star, 'height', `${size}px`);
        this.renderer.setStyle(star, 'left', `${x}px`);
        this.renderer.setStyle(star, 'top', `${y}px`);
        this.renderer.setStyle(star, 'animationDuration', `${duration}s`);
        this.renderer.appendChild(container, star);
      }
    }
  }

  getRandomVideoUrl(): string {
    if (!this.videoIds?.length) return '';
    const randomIndex = Math.floor(Math.random() * this.videoIds.length);
    const randomId = this.videoIds[randomIndex];
    return this.formatVideo(randomId);
  }

  onCode(code: string) {
    if (code.length === 6) {
      this.codeOrPassword = code;
      this.onSignInClicked();
    }
  }
}



